import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * Page de callback OAuth Microsoft 365
 * Cette page est appelée par le backend après authentification Microsoft
 * Elle récupère le token depuis l'URL, le sauvegarde, et redirige vers le dashboard
 */
export default function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(true);
  const processedRef = useRef(false);

  useEffect(() => {
    // Empêcher le traitement multiple
    if (processedRef.current) {
      return;
    }
    
    processedRef.current = true;
    
    const processCallback = async () => {
      try {
        // Vérifier si erreur OAuth
        const errorParam = searchParams.get('error');
        const messageParam = searchParams.get('message');
        
        if (errorParam) {
          console.error('OAuth Error:', errorParam, messageParam);
          setError(messageParam || "Erreur d'authentification Microsoft 365");
          setProcessing(false);
          
          // Rediriger vers login après 3 secondes
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 3000);
          return;
        }
        
        // Récupérer le token depuis l'URL
        const token = searchParams.get('token');
        
        if (!token) {
          console.error('No token in callback URL');
          setError('Token manquant dans la réponse OAuth');
          setProcessing(false);
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 3000);
          return;
        }
        
        console.log('✅ Token reçu depuis backend OAuth');
        
        // Stocker le token dans localStorage
        localStorage.setItem('mlx_token', token);
        
        // Décoder le JWT pour extraire les informations utilisateur
        // Note: Décodage simple base64 (pas de vérification car déjà fait côté backend)
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          console.log('✅ Token décodé:', payload);
          
          const userData = {
            id: payload.sub,
            email: payload.email,
            nom: payload.nom,
            role: payload.role,
            is_guest: payload.is_guest || false
          };
          
          // Sauvegarder les données utilisateur
          localStorage.setItem('mlx_user', JSON.stringify(userData));
          
          console.log('✅ Utilisateur sauvegardé:', userData);
          
          // Mettre à jour le contexte d'authentification
          setUser(userData);
          
          console.log('✅ AuthContext mis à jour, redirection vers dashboard...');
          
          // Rediriger vers le dashboard (remplacer l'historique pour éviter de revenir sur cette page)
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 100);
          
        } catch (decodeError) {
          console.error('Erreur lors du décodage du token:', decodeError);
          setError('Token invalide reçu du serveur');
          setProcessing(false);
          setTimeout(() => {
            navigate('/login', { replace: true });
          }, 3000);
        }
        
      } catch (err) {
        console.error('Erreur lors du traitement du callback OAuth:', err);
        setError('Erreur inattendue lors de la connexion');
        setProcessing(false);
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      }
    };
    
    processCallback();
  }, [searchParams, setUser, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{background:'var(--bg-primary)'}}>
      <div className="text-center px-8">
        {processing && !error && (
          <>
            {/* Logo */}
            <div className="flex items-center justify-center mb-8">
              <img src="/branding/logo-white.png" alt="Adalia" className="h-20 w-auto" />
            </div>
            
            {/* Spinner + Message */}
            <div className="mb-6">
              <div className="inline-block w-12 h-12 border-4 border-[#C9A227] border-t-transparent rounded-full animate-spin"></div>
            </div>
            <h2 className="text-xl font-serif mb-2" style={{color:'var(--text-primary)'}}>
              Finalisation de la connexion...
            </h2>
            <p className="text-[13px]" style={{color:'var(--text-secondary)'}}>
              Vous allez être redirigé vers votre tableau de bord
            </p>
          </>
        )}
        
        {error && (
          <>
            {/* Logo */}
            <div className="flex items-center justify-center mb-8">
              <img src="/branding/logo-white.png" alt="Adalia" className="h-20 w-auto" />
            </div>
            
            {/* Erreur */}
            <div className="max-w-md mx-auto px-6 py-4 rounded-[12px] text-[14px] text-[#DC2626] mb-6" 
                 style={{background:'rgba(220,38,38,0.1)', border:'1px solid rgba(220,38,38,0.2)'}}>
              <div className="font-semibold mb-2">Échec de l'authentification</div>
              <div>{error}</div>
            </div>
            
            <p className="text-[13px]" style={{color:'var(--text-secondary)'}}>
              Redirection vers la page de connexion...
            </p>
          </>
        )}
      </div>
    </div>
  );
}
